import { Icon, IconSize, IconVariants, Image } from "@next-platform/client-common-spicy-ui";
import React, { useCallback, useEffect, useState } from "react";

import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils";

const DEFAULT_TEST_ID = "DiscoverChatbotArtwork";

type Props = {
  readonly name: string;
  readonly avatarUrl?: string;
  readonly isAvatarBlurred: boolean;
  readonly testID?: string;
};

const ARTWORK_FALLBACK_BACKGROUND = "bg-gray-100";
const ARTWORK_FALLBACK_TEXT_COLOR = "text-gray-400";
const ARTWORK_ON_MEDIA_COLOR = "#fff";

// Classname constants
const CLASS_ROOT =
  "relative sm:overflow-hidden object-cover object-[center_top] aspect-square sm:aspect-[6/7]  w-full sm:w-[200px] md:w-[300px] rounded-[16px]";
const CLASS_FALLBACK_CONTAINER = `absolute inset-0 flex items-center justify-center ${ARTWORK_FALLBACK_BACKGROUND}`;
const CLASS_FALLBACK_TEXT = `text-[34px] font-extrabold ${ARTWORK_FALLBACK_TEXT_COLOR}`;
const CLASS_IMAGE_BASE = "w-full h-full object-cover rounded-[16px]";
const CLASS_IMAGE_BLURRED = "filter blur-2xl";
const CLASS_NSFW_VEIL = "absolute inset-0 flex items-center justify-center pointer-events-none";

const readInitials = (name: string): string => {
  const words = name.split(/[\s&]+/).filter(Boolean);
  return `${words[0]?.[0] ?? "?"}${words[1]?.[0] ?? ""}`.toUpperCase();
};

/** Fills its parent with the chatbot avatar; the card art and the profile overlay hero share it. */
const DiscoverChatbotArtwork = ({ name, avatarUrl, isAvatarBlurred, testID }: Props) => {
  const [stateIsImageFailed, setStateIsImageFailed] = useState(false);

  const fullAvatarUrl = avatarUrl
    ? getAvatarUrl(avatarUrl, {
        preset: AvatarPreset.AVATAR_640X640,
      })
    : undefined;

  useEffect(() => {
    setStateIsImageFailed(false);
  }, [avatarUrl]);

  const handleImageError = useCallback(() => setStateIsImageFailed(true), []);
  const isShowFallback = !avatarUrl || stateIsImageFailed;

  const dataTestId = testID ?? DEFAULT_TEST_ID;

  return (
    <div className={CLASS_ROOT} data-testid={dataTestId}>
      {isShowFallback ? (
        <div className={CLASS_FALLBACK_CONTAINER} data-testid={`${dataTestId}-Fallback`}>
          <span className={CLASS_FALLBACK_TEXT}>{readInitials(name)}</span>
        </div>
      ) : (
        <Image
          src={fullAvatarUrl || ""}
          alt={name}
          className={`${CLASS_IMAGE_BASE} ${isAvatarBlurred ? CLASS_IMAGE_BLURRED : ""}`}
          onError={handleImageError}
          data-testid={`${dataTestId}-Image`}
        />
      )}

      {isAvatarBlurred ? (
        <div className={CLASS_NSFW_VEIL} data-testid={`${dataTestId}-NsfwVeil`}>
          <Icon icon={IconVariants.EYE_OFF} size={IconSize.XLARGE} color={ARTWORK_ON_MEDIA_COLOR} />
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(DiscoverChatbotArtwork);
